import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PlusIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { quotesApi, productsApi, cpqCalculateApi, type CpqViolation, type QuoteLineItemRecord } from '../../lib/api'
import { CpqViolationIndicator } from './CpqViolationIndicator'

interface Props {
  quoteId: string
  lineItems: QuoteLineItemRecord[]
  docCurrency: string
  isEditable: boolean
  onItemsChange?: () => void
}

interface EditingItem {
  id: string
  quantity: string
  discountPercent: string
  offerPrice: string
}

interface CpqLineResult {
  violations: CpqViolation[]
  listPrice: number
  offerPrice: number
  discountPercent: number
  lineTotal: number
}

export function LineItemEditor({ quoteId, lineItems, docCurrency, isEditable, onItemsChange }: Props) {
  const queryClient = useQueryClient()
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null)
  const [showProductSearch, setShowProductSearch] = useState(false)
  const [productSearch, setProductSearch] = useState('')
  const [cpqResults, setCpqResults] = useState<Record<string, CpqLineResult>>({})

  // Search products
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['products', 'search', productSearch],
    queryFn: () => productsApi.search(productSearch, 10),
    enabled: productSearch.length >= 2,
  })

  // Add line item mutation
  const addItemMutation = useMutation({
    mutationFn: (data: { productId: string; itemCode: string; itemDescription: string; quantity: string; listPrice: string; offerPrice: string; discountPercent: string; lineTotal: string; lineNum: number }) =>
      quotesApi.addLineItem(quoteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', quoteId] })
      setShowProductSearch(false)
      setProductSearch('')
      onItemsChange?.()
    },
  })

  // Update line item mutation
  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: Partial<QuoteLineItemRecord> }) =>
      quotesApi.updateLineItem(quoteId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', quoteId] })
      setEditingItem(null)
      onItemsChange?.()
    },
  })

  // Delete line item mutation
  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => quotesApi.deleteLineItem(quoteId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', quoteId] })
      onItemsChange?.()
    },
  })

  const formatPrice = (price: string | number | null, currency: string = 'EUR') => {
    if (price === null || price === undefined) return '-'
    const numPrice = typeof price === 'string' ? parseFloat(price) : price
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(numPrice)
  }

  const handleAddProduct = async (product: {
    id: string
    itemCode: string
    itemName: string
    defaultPrice: string | null
  }) => {
    const quantity = '1'
    const listPrice = product.defaultPrice || '0'
    const offerPrice = listPrice
    const discountPercent = '0'
    const lineTotal = listPrice

    // Calculate CPQ for this line
    try {
      const cpqResult = await cpqCalculateApi.calculateLine({
        productId: product.id,
        quantity: parseFloat(quantity),
        requestedDiscountPercent: 0,
      })

      addItemMutation.mutate({
        productId: product.id,
        itemCode: product.itemCode,
        itemDescription: product.itemName,
        quantity,
        listPrice: cpqResult.listPrice.toString(),
        offerPrice: cpqResult.offerPrice.toString(),
        discountPercent: cpqResult.discountPercent.toString(),
        lineTotal: cpqResult.lineTotal.toString(),
        lineNum: lineItems.length,
      })
    } catch {
      // Fallback without CPQ calculation
      addItemMutation.mutate({
        productId: product.id,
        itemCode: product.itemCode,
        itemDescription: product.itemName,
        quantity,
        listPrice,
        offerPrice,
        discountPercent,
        lineTotal,
        lineNum: lineItems.length,
      })
    }
  }

  const handleStartEdit = (item: QuoteLineItemRecord) => {
    setEditingItem({
      id: item.id,
      quantity: item.quantity,
      discountPercent: item.discountPercent || '0',
      offerPrice: item.offerPrice,
    })
  }

  const handleCancelEdit = () => {
    setEditingItem(null)
    setCpqResults({})
  }

  const handleSaveEdit = async () => {
    if (!editingItem) return

    const item = lineItems.find(i => i.id === editingItem.id)
    if (!item) return

    const quantity = parseFloat(editingItem.quantity) || 1
    const listPrice = parseFloat(item.listPrice || '0')
    const discountPercent = parseFloat(editingItem.discountPercent) || 0
    const offerPrice = parseFloat(editingItem.offerPrice) || listPrice * (1 - discountPercent / 100)
    const lineTotal = offerPrice * quantity

    updateItemMutation.mutate({
      itemId: editingItem.id,
      data: {
        quantity: quantity.toString(),
        discountPercent: discountPercent.toString(),
        offerPrice: offerPrice.toString(),
        lineTotal: lineTotal.toString(),
      },
    })
  }

  const handleQuantityChange = async (value: string) => {
    if (!editingItem) return

    const item = lineItems.find(i => i.id === editingItem.id)
    if (!item) return

    setEditingItem({ ...editingItem, quantity: value })

    const quantity = parseFloat(value) || 1
    const discountPercent = parseFloat(editingItem.discountPercent) || 0

    if (item.productId && quantity > 0) {
      try {
        const cpqResult = await cpqCalculateApi.calculateLine({
          productId: item.productId,
          quantity,
          requestedDiscountPercent: discountPercent,
        })
        setCpqResults({
          ...cpqResults,
          [editingItem.id]: cpqResult,
        })
        setEditingItem({
          ...editingItem,
          quantity: value,
          offerPrice: cpqResult.offerPrice.toString(),
        })
      } catch {
        // Fallback calculation
        const listPrice = parseFloat(item.listPrice || '0')
        const offerPrice = listPrice * (1 - discountPercent / 100)
        setEditingItem({
          ...editingItem,
          quantity: value,
          offerPrice: offerPrice.toString(),
        })
      }
    }
  }

  const handleDiscountChange = async (value: string) => {
    if (!editingItem) return

    const item = lineItems.find(i => i.id === editingItem.id)
    if (!item) return

    setEditingItem({ ...editingItem, discountPercent: value })

    const quantity = parseFloat(editingItem.quantity) || 1
    const discountPercent = parseFloat(value) || 0

    if (item.productId && quantity > 0) {
      try {
        const cpqResult = await cpqCalculateApi.calculateLine({
          productId: item.productId,
          quantity,
          requestedDiscountPercent: discountPercent,
        })
        setCpqResults({
          ...cpqResults,
          [editingItem.id]: cpqResult,
        })
        setEditingItem({
          ...editingItem,
          discountPercent: value,
          offerPrice: cpqResult.offerPrice.toString(),
        })
      } catch {
        // Fallback calculation
        const listPrice = parseFloat(item.listPrice || '0')
        const offerPrice = listPrice * (1 - discountPercent / 100)
        setEditingItem({
          ...editingItem,
          discountPercent: value,
          offerPrice: offerPrice.toString(),
        })
      }
    }
  }

  const handleDelete = (itemId: string) => {
    if (confirm('Remove this line item?')) {
      deleteItemMutation.mutate(itemId)
    }
  }

  return (
    <div className="space-y-4">
      {/* Line Items Table */}
      {lineItems.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Qty
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  List Price
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Discount %
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Offer Price
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                {isEditable && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lineItems.map((item) => {
                const isEditing = editingItem?.id === item.id
                const cpqResult = cpqResults[item.id]
                const quantity = isEditing ? parseFloat(editingItem.quantity) || 1 : parseFloat(item.quantity)
                const offerPrice = isEditing ? parseFloat(editingItem.offerPrice) : parseFloat(item.offerPrice)
                const lineTotal = quantity * offerPrice

                return (
                  <tr key={item.id} className={isEditing ? 'bg-blue-50' : ''}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {item.lineNum + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {item.itemCode || '-'}
                        {cpqResult?.violations && cpqResult.violations.length > 0 && (
                          <CpqViolationIndicator violations={cpqResult.violations} compact />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                      {item.itemDescription}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={editingItem.quantity}
                          onChange={(e) => handleQuantityChange(e.target.value)}
                          className="w-20 rounded-md border-gray-300 text-right text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                        />
                      ) : (
                        <span onClick={() => isEditable && handleStartEdit(item)} className={isEditable ? 'cursor-pointer hover:text-brand-600' : ''}>
                          {item.quantity}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                      {formatPrice(item.listPrice, docCurrency)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={editingItem.discountPercent}
                          onChange={(e) => handleDiscountChange(e.target.value)}
                          className="w-20 rounded-md border-gray-300 text-right text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500"
                        />
                      ) : (
                        <span onClick={() => isEditable && handleStartEdit(item)} className={isEditable ? 'cursor-pointer hover:text-brand-600' : ''}>
                          {item.discountPercent}%
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                      {isEditing ? (
                        <span className="font-medium">
                          {formatPrice(editingItem.offerPrice, docCurrency)}
                        </span>
                      ) : (
                        formatPrice(item.offerPrice, docCurrency)
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {isEditing ? formatPrice(lineTotal.toString(), docCurrency) : formatPrice(item.lineTotal, docCurrency)}
                    </td>
                    {isEditable && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={handleSaveEdit}
                              disabled={updateItemMutation.isPending}
                              className="text-green-600 hover:text-green-800 text-xs font-medium"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="text-gray-500 hover:text-gray-700 text-xs font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={deleteItemMutation.isPending}
                            className="text-red-500 hover:text-red-700"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* No items message */}
      {lineItems.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No line items yet.</p>
          {isEditable && (
            <p className="text-sm text-gray-400 mt-1">Add products using the search below.</p>
          )}
        </div>
      )}

      {/* Add Product Section */}
      {isEditable && (
        <div className="border-t pt-4">
          {!showProductSearch ? (
            <button
              onClick={() => setShowProductSearch(true)}
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              <PlusIcon className="h-4 w-4" />
              Add Product
            </button>
          ) : (
            <div className="space-y-3">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Search by product code or name..."
                  className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  autoFocus
                />
              </div>

              {productSearch.length >= 2 && (
                <div className="bg-white border rounded-md shadow-sm max-h-60 overflow-y-auto">
                  {searchLoading ? (
                    <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
                  ) : searchResults && searchResults.length > 0 ? (
                    <ul className="divide-y divide-gray-100">
                      {searchResults.map((product) => (
                        <li
                          key={product.id}
                          onClick={() => handleAddProduct(product)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {product.itemCode}
                              </span>
                              <span className="mx-2 text-gray-400">-</span>
                              <span className="text-sm text-gray-600">
                                {product.itemName}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {formatPrice(product.defaultPrice, docCurrency)}
                            </span>
                          </div>
                          {product.shortDescription && (
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {product.shortDescription}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">No products found</div>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowProductSearch(false)
                    setProductSearch('')
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
